import {PaginationConfig} from 'antd/lib/pagination';
import locales from '../locales';
import {GridRequest, PageResponseEnvelope} from '../api/dtos/dtos';
import {ColumnFilterItem, ColumnProps, SorterResult, SortOrder} from 'antd/lib/table';
import * as React from 'react';
import SearchFilter, {SearchFilterProps} from '../common/SearchFilter';
import {WithId} from './utils';

export interface GridState<T> {
  page: number;
  pageSize?: number;
  total: number;
  data: T[];
  sortOrder?: SortOrder;
  sortColumnKey?: string;
  filters?: { [key: string]: string[] }
}

export const defaultPaginationConfig = (state: GridState<{}>): PaginationConfig => ({
  defaultPageSize: state.pageSize,
  total: state.total,
  current: state.page,
  showSizeChanger: true,
  showTotal: (total) => (<>{locales.total} <b>{total}</b> {locales.entry}</>),
});

export const initialGridState = (pageSize?: number) => ({
  pageSize: pageSize ? pageSize : 40,
  total: 0,
  page: 1,
  data: [],
});

export const toGridRequest = <T extends {}>(state: GridState<T>): GridRequest => {
  return {
    page: state.page - 1,
    pageSize: state.pageSize,
    sortOrder: state.sortOrder,
    sortColumnKey: state.sortColumnKey,
    filters: state.filters,
  };
};

export const toGridState = <T extends {}>(response: PageResponseEnvelope<T>) => {
  return {
    data: response.results.content,
    page: response.results.currentPage + 1,  // api page index starts from 0, ant.design's from 1
    total: response.results.total,
  }
};

export const handleOnGridChange =
  <T extends {}>(state: GridState<T>, callback: (newState: GridState<T>) => void) =>
    (pagination: PaginationConfig, filters: { [key: string]: string[] }, sorter: SorterResult<any>) => {
      callback({
        page: pagination.current!!,
        pageSize: pagination.pageSize,
        total: state.total,
        data: state.data,
        sortOrder: sorter.order,
        sortColumnKey: sorter.columnKey,
        filters,
      });
    };

export const idRowKeyFn = (record: WithId) => String(record.id);
export const indexRowKeyFn = <T extends {}>(record: T, index: number) => String(index);

export const simpleColumn = <T extends {}>(title: string, render: (record: T) => React.ReactNode, width?: number,): ColumnProps<T> => ({
  title,
  key: title,
  width,
  render: (text, record) => render(record)
});

export const noTitleColumn = <T extends {}>(
  key: string,
  render: (record: T) => React.ReactNode,
  width?: number
): ColumnProps<T> => ({
  key,
  width,
  render: (text, record) => render(record),
});

export const sortableColumn = <T extends {}>(
  title: string,
  key: string,
  state: GridState<T>,
  render: (record: T) => React.ReactNode,
  width?: number,
): ColumnProps<T> => ({
  ...simpleColumn(title, render, width),
  key,
  sorter: true,
  sortOrder: key === state.sortColumnKey ? state.sortOrder : undefined,
});

export const filterableColumn = <T extends {}>(
  title: string,
  key: string,
  state: GridState<T>,
  render: (record: T) => React.ReactNode,
  filters: ColumnFilterItem[],
  filterMultiple: boolean = true,
): ColumnProps<T> => ({
  ...simpleColumn(title, render),
  key,
  filters,
  filterMultiple,
  filteredValue: state.filters && state.filters[key],
});

export const searchableSortableColumn = <T extends {}>(
  title: string,
  key: string,
  state: GridState<T>,
  render: (record: T) => React.ReactNode,
  width?: number,
): ColumnProps<T> => {
  let filter: SearchFilter | null;
  return {
    ...sortableColumn(title, key, state, render, width),
    filterDropdown: (props: SearchFilterProps) => {
      return (<SearchFilter {...props} ref={(instance) => filter = instance}/>);
    },
    onFilterDropdownVisibleChange: (visible: boolean) => {
      if (visible) {
        setTimeout(() => {
          if (filter) {
            filter.focus();
          }
        }, 250);  // 250ms to avoid filter dialog flickering on Android
      }
    },
    filteredValue: state.filters && state.filters[key],
    sorter: true,
  }
};

export const searchableColumn = <T extends {}>(
  title: string,
  key: string,
  state: GridState<T>,
  render: (record: T) => React.ReactNode
): ColumnProps<T> => {
  let filter: SearchFilter | null;
  return {
    ...simpleColumn(title, render),
    key,
    filterDropdown: (props: SearchFilterProps) => {
      return (<SearchFilter {...props} ref={(instance) => filter = instance}/>);
    },
    onFilterDropdownVisibleChange: (visible: boolean) => {
      if (visible) {
        setTimeout(() => {
          if (filter) {
            filter.focus();
          }
        }, 250);  // 250ms to avoid filter dialog flickering on Android
      }
    },
    filteredValue: state.filters && state.filters[key],
  }
};


// WEBPACK FOOTER //
// ./src/utils/table-utils.tsx